import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../components/AppLayout';
import { MaterialService } from '../services/materialService';
import { CategoryService } from '../services/categoryService';
import { Material, Category } from '../types';
import '../styles/MaterialCatalogPage.css';

export const MaterialCatalogPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [materials, setMaterials] = useState<Material[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'price' | 'newest'>('name');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [materialsData, categoriesData] = await Promise.all([
        MaterialService.getAllMaterials(),
        CategoryService.getAllCategories(),
      ]);

      setMaterials(materialsData);
      setCategories(categoriesData);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredMaterials = materials.filter((material) => {
    // Filter by category
    if (selectedCategory && material.categoryId !== selectedCategory) {
      return false;
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        material.name.toLowerCase().includes(query) ||
        material.color.toLowerCase().includes(query) ||
        material.size.toLowerCase().includes(query)
      );
    }

    return true;
  });

  // Sort materials
  const sortedMaterials = [...filteredMaterials].sort((a, b) => {
    switch (sortBy) {
      case 'price':
        return a.basePrice - b.basePrice;
      case 'newest':
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      case 'name':
      default:
        return a.name.localeCompare(b.name);
    }
  });

  const getCategoryName = (categoryId: string): string => {
    const category = categories.find((c) => c.id === categoryId);
    return category?.name || 'Unknown';
  };

  const formatPrice = (price: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  if (loading) {
    return (
      <div className="catalog-page">
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <p>Loading materials...</p>
        </div>
      </div>
    );
  }

  return (
    <AppLayout>
      <div className="catalog-page">
        <div className="catalog-header">
          <h1>Material Catalog</h1>
          <p className="subtitle">Browse and explore our available materials</p>
        </div>

      <div className="catalog-container">
        {/* Sidebar - Filters */}
        <aside className="catalog-sidebar">
          <div className="filter-section">
            <h3>Filter by Category</h3>
            <div className="category-list">
              <button
                className={`category-btn ${selectedCategory === null ? 'active' : ''}`}
                onClick={() => setSelectedCategory(null)}
              >
                All Categories
              </button>
              {categories.map((category) => (
                <button
                  key={category.id}
                  className={`category-btn ${selectedCategory === category.id ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(category.id)}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          <div className="filter-section">
            <h3>Sort By</h3>
            <div className="sort-options">
              <label>
                <input
                  type="radio"
                  value="name"
                  checked={sortBy === 'name'}
                  onChange={(e) => setSortBy(e.target.value as any)}
                />
                Name (A-Z)
              </label>
              <label>
                <input
                  type="radio"
                  value="price"
                  checked={sortBy === 'price'}
                  onChange={(e) => setSortBy(e.target.value as any)}
                />
                Price (Low to High)
              </label>
              <label>
                <input
                  type="radio"
                  value="newest"
                  checked={sortBy === 'newest'}
                  onChange={(e) => setSortBy(e.target.value as any)}
                />
                Newest
              </label>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="catalog-main">
          {/* Search Bar */}
          <div className="search-container">
            <input
              type="text"
              placeholder="Search by name, color, or size..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            <span className="result-count">
              {sortedMaterials.length} result{sortedMaterials.length !== 1 ? 's' : ''}
            </span>
          </div>

          {error && (
            <div
              style={{
                padding: '12px 16px',
                background: '#fee2e2',
                color: '#c33',
                marginBottom: '20px',
                borderRadius: '6px',
              }}
            >
              {error}
            </div>
          )}

          {/* Materials Grid */}
          {sortedMaterials.length === 0 ? (
            <div className="empty-state">
              <p>No materials found matching your criteria.</p>
              {(selectedCategory || searchQuery) && (
                <button
                  onClick={() => {
                    setSelectedCategory(null);
                    setSearchQuery('');
                  }}
                  className="btn-reset-filters"
                >
                  Reset Filters
                </button>
              )}
            </div>
          ) : (
            <div className="materials-grid">
              {sortedMaterials.map((material) => (
                <div key={material.id} className="material-card">
                  <div className="material-header">
                    <h3>{material.name}</h3>
                    <span className="category-badge">
                      {getCategoryName(material.categoryId)}
                    </span>
                  </div>

                  <div className="material-details">
                    <div className="detail-row">
                      <span className="label">Color:</span>
                      <span className="value">{material.color}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">Size:</span>
                      <span className="value">{material.size}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">Thickness:</span>
                      <span className="value">{material.thickness}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">Weight:</span>
                      <span className="value">{material.weight} lbs</span>
                    </div>
                  </div>

                  <div className="material-pricing">
                    <div className="price-item">
                      <span className="price-label">Base Price</span>
                      <span className="price-value">
                        {formatPrice(material.basePrice)}
                      </span>
                    </div>
                    <div className="price-item">
                      <span className="price-label">Markup</span>
                      <span className="price-value">
                        {((material.markupFactor - 1) * 100).toFixed(0)}%
                      </span>
                    </div>
                    <div className="price-item final">
                      <span className="price-label">Final Price</span>
                      <span className="price-value">
                        {formatPrice(
                          material.basePrice * material.markupFactor
                        )}
                      </span>
                    </div>
                  </div>

                  <div className="material-actions">
                    <button
                      onClick={() => navigate('/quotes/new')}
                      className="btn-add-to-quote"
                    >
                      + Add to Quote
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>
      </div>
    </AppLayout>
  );
};
